import React, {useEffect, useMemo, useState} from 'react';
import {Button, Collapse, Flex, Popconfirm, Segmented, Space, Spin, Tag, Typography} from 'antd';
import classNames from 'classnames';
import {AssigneeModel} from '@/typings/models/Assignee';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import {MinusCircleOutlined, PlusOutlined} from '@ant-design/icons';
import ToggleCard from '@/components/ToggleCard/ToggleCard';
import styles from './AgentDrawer.less';
import {ProForm} from '@ant-design/pro-components';
import UserWorkingHours from '@/components/UserDrawer/Edit/UserWorkingHours';
import {AvailabilityTimezoneHint} from '@/components/Availability/AvailabilityTimezone';
import {debounce} from 'lodash';
import {
  deleteCustomAvailabilityRequest,
  updateCustomAvailabilityRequest,
} from '@/services/api/user';
import {processAPIResponseNow} from '@/utils/request';
import {useDispatch} from 'umi';
import BranchServiceTreeSelect from '@/components/Filters/BranchServiceSelect';
import {
  ManagedBranchesBlock,
  ManagedServicesBlock,
  useManagementAssignments,
  useOpenLocationDrawer,
} from './AgentManagement';

const { Text } = Typography;

interface AgentServicesTabProps {
  agent: AssigneeModel;
  onDirty?: () => void;
}

const AgentServiceAvailability = ({ agent, service, initialAvailability, onToggle, onDirty }) => {
  const [hasCustom, setHasCustom] = useState(!!initialAvailability);
  const dispatch = useDispatch();
  const [form] = ProForm.useForm();

  useEffect(() => {
    form.setFieldsValue({ availability: initialAvailability });
    setHasCustom(!!initialAvailability);
  }, [initialAvailability]);

  const getPayload = (payload) => {
    if (!payload.extra_days) {
      payload.extra_days = [];
    }
    // Capacity is not part of a custom calendar (the field is hidden below) —
    // never send a stale value that lingered in the form state.
    delete payload.capacity;
    return payload
  }
  const debouncedUpdate = useMemo(
    () =>
      debounce((_, allValues) => {
        onDirty?.();
        updateCustomAvailabilityRequest({
          params: { userId: agent.id, branchServiceId: service.id },
          payload: getPayload(allValues.availability),
        }).then((response) => {
          processAPIResponseNow(
            response,
            {
              showSuccessMessage: true,
              successEntity: INTL_ENTITY.WORKING_HOURS,
              onSuccess() {

              },
            },
            'data',
          );
        });
      }, 400),
    [agent.id, service.id],
  );

  const handleToggle = (checked: boolean) => {
    setHasCustom(checked);
    onToggle?.(checked);
    if (!checked) {
      onDirty?.();
      deleteCustomAvailabilityRequest({
        params: { userId: agent.id, branchServiceId: service.id },
      }).then((response) => {
        processAPIResponseNow(
          response,
          {
            onSuccess() {
              dispatch({
                type: 'user/fetchSingleAgent',
                payload: { agentId: agent.id },
              });
            },
          },
          'data',
        );
      });
    }
  };

  return (
    <ProForm
      form={form}
      className={styles.serviceCustomAvailabilityForm}
      submitter={false}
      initialValues={{ availability: initialAvailability }}
      onValuesChange={debouncedUpdate}
    >
      <ToggleCard
        title={
          <Space>
            <strong>
              <F
                id="pages.users.custom_service_availability_toggle.title"
                defaultMessage={'Custom schedule'}
              />
            </strong>
            <Text type={'secondary'}>
              <small>
                <F
                  id="pages.users.custom_service_availability_toggle.description"
                  defaultMessage={
                    'override general availability for this service only'
                  }
                />
              </small>
            </Text>
          </Space>
        }
        checked={hasCustom}
        onChange={handleToggle}
      />
      {hasCustom && (
        <>
          <div style={{ margin: '8px 0 12px' }}>
            <AvailabilityTimezoneHint timezones={service.branch?.timezone ? [service.branch.timezone] : []} />
          </div>
          <UserWorkingHours hideOffDays hideCapacity user={agent} />
        </>
      )}
    </ProForm>
  );
};

const AgentServicesTab: React.FC<AgentServicesTabProps> = ({ agent, onDirty }) => {
  const loading = false;
  const dispatch = useDispatch();
  const [form] = ProForm.useForm();
  const [section, setSection] = useState<'services' | 'management'>('services');
  const [customOverrides, setCustomOverrides] = useState<
    Record<string, boolean>
  >({});

  const management = useManagementAssignments(agent, onDirty);
  const { canOpenBranch, canOpenService, openBranch, openService } = useOpenLocationDrawer();

  const currentServiceIds = useMemo(
    () => agent.services?.map((s) => s.id) || [],
    [agent.services],
  );

  const handleUpdateServices = (
    services: (string | undefined)[],
    touchedServiceIds: (string | undefined)[] = services,
  ) => management.patchAssignments({ services }, touchedServiceIds);

  const handleDeleteService = (serviceId: string) => {
    const newServices =
      agent.services?.filter((s) => s.id !== serviceId).map((s) => s.id) || [];
    // The removed service is what changed — keep it in the touched list.
    handleUpdateServices(newServices, [...newServices, serviceId]);
  };

  useEffect(() => {
    return () => {
      dispatch({
        type: 'user/fetchSingleAgent',
        payload: { agentId: agent.id },
      });
    };
  }, []);

  const perServiceAvailability = useMemo(
    () =>
      agent.custom_availability?.reduce((acc, availability) => {
        acc[availability.branch_service_id] = availability.custom_availability;
        return acc;
      }, {}),
    [agent],
  );

  const servicesByBranch = useMemo(() => {
    const map = new Map<
      string,
      { branch: { id?: string; name?: string }; services: typeof agent.services }
    >();
    agent.services?.forEach((service) => {
      const key = service.branch?.id ?? service.branch?.name ?? 'unknown';
      if (!map.has(key)) {
        map.set(key, { branch: service.branch ?? {}, services: [] });
      }
      map.get(key)!.services.push(service);
    });
    return Array.from(map.values())
      .sort((a, b) => (a.branch?.name ?? '').localeCompare(b.branch?.name ?? ''))
      .map((group) => ({
        ...group,
        services: [...group.services].sort((a, b) =>
          (a.service_type?.name ?? '').localeCompare(b.service_type?.name ?? ''),
        ),
      }));
  }, [agent.services]);

  const servicesContent = (
    <>
      {servicesByBranch.map(({ branch, services }) => (
        <div key={branch?.id ?? branch?.name ?? 'unknown'} style={{ marginBottom: 24 }}>
          <Typography.Title
            level={5}
            style={{ marginBottom: 12, marginTop: 0 }}
            className={classNames({ [styles.clickableHeading]: canOpenBranch })}
            onClick={() => openBranch(branch)}
          >
            {branch?.name}
          </Typography.Title>
          {services.map((service) => {
        const isCustom =
          customOverrides[service.id] !== undefined
            ? customOverrides[service.id]
            : !!perServiceAvailability?.[service.id];

        return (
          <div className={styles.serviceAgentCollapse} key={service.id}>
            <Collapse key={service.id}>
              <Collapse.Panel
                key={service.id}
                header={
                  <Flex justify={'space-between'} align={'center'}>
                    <Space
                      size="small"
                      className={classNames({ [styles.clickableHeading]: canOpenService })}
                      onClick={(e) => {
                        if (!canOpenService) return;
                        e.stopPropagation();
                        openService(service);
                      }}
                    >
                      <span>{service.service_type?.icon}</span>
                      <Text>{service.service_type?.name}</Text>
                    </Space>
                    <Space size="small" align="center">
                        <Tag
                          bordered={false}
                          style={{ fontSize: 10 }}
                          color={isCustom ? 'lime' : 'default'}
                        >
                          {isCustom ? (
                            <F
                              id="pages.users.custom_schedule"
                              defaultMessage="Custom schedule"
                            />
                          ) : (
                            <F
                              id="pages.users.general_schedule"
                              defaultMessage="General schedule"
                            />
                          )}
                        </Tag>
                      <Popconfirm
                        onPopupClick={(e) => e.stopPropagation()}
                        title={
                          <F
                            entity={INTL_ENTITY.SERVICE}
                            action={INTL_ACTION.REMOVE_CONFIRMATION}
                          />
                        }
                        onConfirm={() => handleDeleteService(service.id!)}
                      >
                        <Button
                          onClick={(e) => e.stopPropagation()}
                          size={'small'}
                          icon={<MinusCircleOutlined />}
                          type={'text'}
                          danger
                        />
                      </Popconfirm>
                    </Space>
                  </Flex>
                }
              >
                  <AgentServiceAvailability
                    agent={agent}
                    service={service}
                    onDirty={onDirty}
                    initialAvailability={perServiceAvailability?.[service.id]}
                    onToggle={(checked) =>
                      setCustomOverrides((prev) => ({
                        ...prev,
                        [service.id]: checked,
                      }))
                    }
                  />
              </Collapse.Panel>
            </Collapse>
          </div>
        );
          })}
        </div>
      ))}
      <div style={{ marginTop: 16, paddingTop: 16, borderTop: agent.services?.length > 0 ? "1px solid #aaa" : "none" }}>
        <ProForm form={form} submitter={false}>
          <div className={styles.addService}>
            <BranchServiceTreeSelect
              placeholder={
                <Space>
                  <PlusOutlined/>
                  <F entity={INTL_ENTITY.SERVICE} action={INTL_ACTION.ADD} />
                </Space>
              }
              name="services"
              inScope={true}
              withStaff={false}
              filterMode="service"
              filterKey="service"
              filterServices={currentServiceIds}
              selectProps={{
                mode: 'multiple',
                variant: 'borderless',
                size: 'large',
              }}
              fieldProps={{
                onDropdownVisibleChange: (visible: boolean) => {
                  if (!visible) {
                    const values = form.getFieldValue('services');
                    if (values && values.length > 0) {
                      handleUpdateServices([...currentServiceIds, ...values]);
                      form.setFieldsValue({ services: [] });
                    }
                  }
                },
              }}
            />
          </div>
        </ProForm>
      </div>
    </>
  );

  const managementContent = (
    <>
      <ManagedBranchesBlock management={management} />
      <ManagedServicesBlock management={management} />
    </>
  );

  return (
    <Spin spinning={loading}>
      <Segmented
        block
        className={styles.sectionSwitch}
        value={section}
        onChange={(value) => setSection(value as 'services' | 'management')}
        options={[
          {
            value: 'services',
            label: <F id={'pages.users.bookableServices.title'} defaultMessage={'Bookable Services'} />,
          },
          {
            value: 'management',
            label: <F id="pages.users.management.title" defaultMessage="Management" />,
          },
        ]}
      />
      {section === 'services' ? servicesContent : managementContent}
    </Spin>
  );
};

export default AgentServicesTab;
